import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, type GameRecord, type User } from '../api';

function ErrorText({ error }: { error: string | null }) {
  if (!error) {
    return null;
  }
  return <p className="auth-error">{error}</p>;
}

export function LoginPage({
  onAuthed,
  onSwitch,
}: {
  onAuthed: (user: User) => void;
  onSwitch: () => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      onAuthed(await api.login(email.trim(), password));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-screen">
      <h1 className="app-title">TayTayA</h1>
      <p className="auth-screen__subtitle">Sign in to play</p>
      <form className="auth-form" onSubmit={submit}>
        <label className="auth-field">
          <span>Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </label>
        <label className="auth-field">
          <span>Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
        </label>
        <ErrorText error={error} />
        <button className="pill-button pill-button--accent" type="submit" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Log in'}
        </button>
      </form>
      <p className="auth-switch">
        No account?{' '}
        <button type="button" className="auth-switch__link" onClick={onSwitch}>
          Create one
        </button>
      </p>
      <p className="auth-version">Beta v0.1.0</p>
    </div>
  );
}

export function SignupPage({
  onAuthed,
  onSwitch,
}: {
  onAuthed: (user: User) => void;
  onSwitch: () => void;
}) {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      onAuthed(await api.signup(username.trim(), email.trim(), password));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-screen">
      <h1 className="app-title">TayTayA</h1>
      <p className="auth-screen__subtitle">Create your account</p>
      <form className="auth-form" onSubmit={submit}>
        <label className="auth-field">
          <span>Username</span>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            autoComplete="username"
          />
        </label>
        <label className="auth-field">
          <span>Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </label>
        <label className="auth-field">
          <span>Password (6+ characters)</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            autoComplete="new-password"
          />
        </label>
        <ErrorText error={error} />
        <button className="pill-button pill-button--accent" type="submit" disabled={submitting}>
          {submitting ? 'Creating…' : 'Sign up'}
        </button>
      </form>
      <p className="auth-switch">
        Already have an account?{' '}
        <button type="button" className="auth-switch__link" onClick={onSwitch}>
          Log in
        </button>
      </p>
      <p className="auth-version">Beta v0.1.0</p>
    </div>
  );
}

export function HistoryPage({
  user,
  onBack,
  onLogout,
}: {
  user: User;
  onBack: () => void;
  onLogout: () => void;
}) {
  const [games, setGames] = useState<GameRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .games()
      .then((list) => {
        if (!cancelled) {
          setGames(list);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Could not load your history.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="account-screen">
      <h1 className="app-title">Account</h1>
      <div className="account-info">
        <p className="account-info__name">{user.username}</p>
        <p className="account-info__email">{user.email}</p>
      </div>
      <div className="account-games">
        <h2 className="account-games__title">History</h2>
        {error ? (
          <p className="auth-error">{error}</p>
        ) : games === null ? (
          <p className="account-games__empty">Loading…</p>
        ) : games.length === 0 ? (
          <p className="account-games__empty">No games yet — play one and it will show up here.</p>
        ) : (
          <ul className="account-games__list">
            {games.map((game) => (
              <li key={game.id} className={`account-game account-game--${game.result}`}>
                <span className="account-game__result">{game.result.toUpperCase()}</span>
                <span className="account-game__mode">
                  {game.mode === 'vs-bot' ? 'Vs Bot' : game.mode === 'online' ? 'Online' : '2 Players'}
                </span>
                <span className="account-game__date">
                  {new Date(game.playedAt).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
